import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Power, ArrowLeft } from 'lucide-react';
import { useTenant } from '@core/tenant';
import { useAuth } from '@core/auth';
import { redirectToAuth } from '@core/auth/authRedirect';
import { LanguageSelector } from '@shared/components/common';
import { cn } from '@shared/utils/cn';
import BookingFooter from '@features/booking/components/BookingFooter';
import { BookingChrome } from '@features/booking/layouts';
import MyReservationsTab from '../components/MyReservationsTab';
import MyProfileTab from '../components/MyProfileTab';

type ProfileTab = 'reservations' | 'profile';

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState<ProfileTab>('reservations');
  const { t } = useTranslation('profile');
  const { restaurant } = useTenant();
  const { authState, isLoading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !authState.isAuthenticated) {
      redirectToAuth('login');
    }
  }, [isLoading, authState.isAuthenticated]);

  if (isLoading || !authState.isAuthenticated) {
    return null;
  }

  return (
    <BookingChrome>
        {/* Header — sticky to viewport top so it stays visible while
            scrolling long reservation lists. Matches the shared
            BookingHeader's sticky treatment (this page uses a custom
            header because the right-side action is logout, not the
            standard account icon). */}
        <header className="booking-header px-6 sticky top-0 z-20 bg-card lg:translucent:bg-transparent max-lg:translucent:backdrop-blur-sm">
          <div className="w-10 flex justify-start">
            <LanguageSelector size="sm" />
          </div>

          <button
            onClick={() => navigate('/')}
            className="cursor-pointer"
            aria-label={t('PROFILE_GO_HOME')}
          >
            {restaurant?.customStyle?.headerImage ? (
              <img
                src={restaurant.customStyle.headerImage}
                alt={restaurant?.name ?? 'Restaurant'}
                className="h-10 max-w-[180px] object-contain"
              />
            ) : (
              <h1 className="text-2xl font-display tracking-wide text-center">
                {restaurant?.name ?? 'Restaurant'}
              </h1>
            )}
          </button>

          <div className="w-10 flex justify-end">
            <button
              className="text-brand flex items-center hover:text-brand-dark transition-colors"
              onClick={() => { logout(); navigate('/'); }}
              aria-label={t('PROFILE_LOGOUT')}
            >
              <Power size={18} />
            </button>
          </div>
        </header>

        {/* Tabs — underline pattern (§3: premium from restraint).
            Previous solid-brand pill was loud and read as dated. The
            text-with-underline matches an editorial register and keeps
            brand color reserved for the primary action and affordance
            markers.

            a11y: these are toggle buttons, not a WAI-ARIA tablist. A
            partial tablist (role="tab" without arrow-key navigation,
            aria-controls, and tabpanel) misleads SR users — they hear
            "tab" but get button-row behavior. `aria-pressed` is the
            honest contract for a 2-option toggle and the cheapest
            correct option per the WAI-ARIA Authoring Practices Guide.
            If this ever grows to N tabs with content panels, complete
            the tablist pattern then. */}
        <div className="px-6 pt-4">
          <div className="flex gap-8 border-b border-border">
            <button
              type="button"
              aria-pressed={activeTab === 'reservations'}
              className={cn(
                'py-3 text-sm font-medium tracking-wide transition-colors border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                activeTab === 'reservations'
                  ? 'text-foreground border-brand'
                  : 'text-foreground/50 border-transparent hover:text-foreground/80',
              )}
              onClick={() => setActiveTab('reservations')}
            >
              {t('MY_RESERVATIONS')}
            </button>
            <button
              type="button"
              aria-pressed={activeTab === 'profile'}
              className={cn(
                'py-3 text-sm font-medium tracking-wide transition-colors border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                activeTab === 'profile'
                  ? 'text-foreground border-brand'
                  : 'text-foreground/50 border-transparent hover:text-foreground/80',
              )}
              onClick={() => setActiveTab('profile')}
            >
              {t('MY_PROFILE')}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="booking-content pt-8 pb-10 flex flex-col items-center min-h-[320px]">
          {activeTab === 'reservations' ? (
            <MyReservationsTab />
          ) : (
            <MyProfileTab />
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4 gap-4">
            <button
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm shrink-0"
              onClick={() => navigate('/')}
              aria-label={t('PROFILE_BACK_TO_RESERVATIONS')}
            >
              <ArrowLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
            <span className="text-base font-medium">
              {t('PROFILE_BACK_TO_RESERVATIONS')}
            </span>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default ProfilePage;
